'use client';

import { useMemo, useState } from 'react';
import Alert24CTA from '@/components/Alert24CTA';
import TrackedCopyButton from '@/components/TrackedCopyButton';
import {
  PATTERNS,
  COMBINED_EXAMPLE,
  COMBINED_FIELDS,
  ERROR_EXAMPLE,
  ERROR_FIELDS,
  ERROR_LEVELS,
  DEFAULT_LOCATIONS,
  CUSTOM_LOG_FORMAT_EXAMPLE,
  ALERT_RULES_DOC_NOTE,
  type NginxPattern,
  type Severity,
} from './lib/data';

const TOOL_SLUG = 'nginx-log-patterns';

const SEVERITY_STYLES: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
  info: 'bg-gray-100 text-gray-700 border-gray-200',
};

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${SEVERITY_STYLES[severity]}`}
    >
      {severity}
    </span>
  );
}

function LogBadge({ log }: { log: 'access' | 'error' }) {
  return (
    <span
      className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${
        log === 'access'
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-purple-50 text-purple-700 border-purple-200'
      }`}
    >
      {log} log
    </span>
  );
}

// Escape a string for safe inline HTML rendering (we build highlighted output
// ourselves, so we must escape user input first to avoid injection).
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface TestResult {
  html: string;
  matchCount: number;
  matchedLines: number;
  totalLines: number;
  error: string | null;
}

function runMatcher(pattern: NginxPattern | null, input: string): TestResult {
  if (!pattern || !input) {
    return { html: '', matchCount: 0, matchedLines: 0, totalLines: 0, error: null };
  }
  let re: RegExp;
  try {
    // Always global + multiline so per-line highlighting works regardless of
    // the pattern's own flags; case-insensitivity is preserved if present.
    const flags = new Set((pattern.flags + 'gm').split(''));
    re = new RegExp(pattern.regex, [...flags].join(''));
  } catch (e) {
    return {
      html: '',
      matchCount: 0,
      matchedLines: 0,
      totalLines: 0,
      error: e instanceof Error ? e.message : 'Invalid pattern',
    };
  }

  const lines = input.replace(/\r\n/g, '\n').split('\n');
  let matchCount = 0;
  let matchedLines = 0;
  const renderedLines = lines.map((line) => {
    if (line === '') return '';
    // Per-line regex so a match cannot span lines.
    const lineRe = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
    let out = '';
    let last = 0;
    let m: RegExpExecArray | null;
    let lineHadMatch = false;
    let guard = 0;
    while ((m = lineRe.exec(line)) !== null) {
      if (guard++ > 5000) break;
      if (m.index >= last) {
        out += escapeHtml(line.slice(last, m.index));
        out +=
          '<mark class="bg-yellow-200 text-yellow-900 rounded px-0.5">' +
          escapeHtml(m[0]) +
          '</mark>';
        last = m.index + m[0].length;
        matchCount++;
        lineHadMatch = true;
      }
      if (m.index === lineRe.lastIndex) lineRe.lastIndex++; // avoid zero-width loop
    }
    out += escapeHtml(line.slice(last));
    if (lineHadMatch) matchedLines++;
    return out;
  });

  return {
    html: renderedLines.join('\n'),
    matchCount,
    matchedLines,
    totalLines: lines.filter((l) => l !== '').length,
    error: null,
  };
}

export default function NginxLogPatternsTool() {
  const [selectedId, setSelectedId] = useState<string>(PATTERNS[0].id);
  const [input, setInput] = useState<string>('');

  const selected = useMemo(
    () => PATTERNS.find((p) => p.id === selectedId) ?? null,
    [selectedId]
  );

  const result = useMemo(() => runMatcher(selected, input), [selected, input]);

  const loadExamples = () => {
    // Seed the textarea with every pattern's example line so the user can see
    // the selected pattern light up its match immediately.
    setInput(PATTERNS.map((p) => p.example).join('\n'));
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Privacy notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
        <p className="text-sm text-blue-900">
          <strong>Private by design:</strong> the pattern matcher runs entirely in your browser.
          Log lines you paste are never uploaded, logged, or stored anywhere.
        </p>
      </div>

      {/* In-page navigation */}
      <nav className="flex flex-wrap gap-2 mb-8 text-sm">
        <a href="#log-format-reference" className="rounded-full border border-gray-300 px-3 py-1 text-[#2813e8] hover:bg-gray-50">
          1. Log format reference
        </a>
        <a href="#pattern-library" className="rounded-full border border-gray-300 px-3 py-1 text-[#2813e8] hover:bg-gray-50">
          2. Pattern library
        </a>
        <a href="#paste-and-test" className="rounded-full border border-gray-300 px-3 py-1 text-[#2813e8] hover:bg-gray-50">
          3. Paste &amp; test
        </a>
      </nav>

      {/* ================= SECTION 1: LOG FORMAT REFERENCE ================= */}
      <section id="log-format-reference" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-[#4B4F58] mb-4">Nginx log format reference</h2>

        {/* Access log — combined */}
        <h3 className="text-lg font-semibold text-[#4B4F58] mb-2">
          Access log &mdash; default <code className="text-sm bg-gray-100 px-1 rounded">combined</code> format
        </h3>
        <p className="text-[#4B4F58] mb-3 text-sm">
          The default access log uses the predefined <code className="bg-gray-100 px-1 rounded">combined</code> format.
          Its <code className="bg-gray-100 px-1 rounded">log_format</code> definition is:
        </p>
        <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto mb-3">
{`log_format combined '$remote_addr - $remote_user [$time_local] '
                    '"$request" $status $body_bytes_sent '
                    '"$http_referer" "$http_user_agent"';`}
        </pre>
        <p className="text-[#4B4F58] mb-2 text-sm font-medium">Example line:</p>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto mb-4 text-[#4B4F58]">
          {COMBINED_EXAMPLE}
        </pre>
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border text-left">Field</th>
                <th className="px-3 py-2 border text-left">Variable</th>
                <th className="px-3 py-2 border text-left">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {COMBINED_FIELDS.map((f) => (
                <tr key={f.variable} className="align-top">
                  <td className="px-3 py-2 border font-mono text-xs text-[#2813e8] whitespace-nowrap">{f.token}</td>
                  <td className="px-3 py-2 border font-mono text-xs whitespace-nowrap">{f.variable}</td>
                  <td className="px-3 py-2 border text-[#4B4F58]">{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Error log */}
        <h3 className="text-lg font-semibold text-[#4B4F58] mb-2">Error log format</h3>
        <p className="text-[#4B4F58] mb-3 text-sm">
          The error log is not configurable like the access log; nginx writes a fixed structure:
          <code className="bg-gray-100 px-1 rounded ml-1">date time [level] pid#tid: *conn message</code>.
        </p>
        <p className="text-[#4B4F58] mb-2 text-sm font-medium">Example line:</p>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto mb-4 text-[#4B4F58]">
          {ERROR_EXAMPLE}
        </pre>
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border text-left">Field</th>
                <th className="px-3 py-2 border text-left">Part</th>
                <th className="px-3 py-2 border text-left">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_FIELDS.map((f) => (
                <tr key={f.variable} className="align-top">
                  <td className="px-3 py-2 border font-mono text-xs text-[#2813e8]">{f.token}</td>
                  <td className="px-3 py-2 border font-mono text-xs whitespace-nowrap">{f.variable}</td>
                  <td className="px-3 py-2 border text-[#4B4F58]">{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Severity levels */}
        <h3 className="text-lg font-semibold text-[#4B4F58] mb-2" id="error-log-levels">
          Error log severity levels
        </h3>
        <p className="text-[#4B4F58] mb-3 text-sm">
          The second argument to <code className="bg-gray-100 px-1 rounded">error_log</code> sets the minimum level
          (e.g. <code className="bg-gray-100 px-1 rounded">error_log /var/log/nginx/error.log warn;</code>). Levels
          run from least to most severe; selecting one logs that level <em>and all more severe ones</em>.
        </p>
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border text-left">Level</th>
                <th className="px-3 py-2 border text-left">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_LEVELS.map((l, i) => (
                <tr key={l.level} className={i >= 4 ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 border font-mono text-xs font-semibold">{l.level}</td>
                  <td className="px-3 py-2 border text-[#4B4F58]">{l.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-1">
            Highlighted rows (<code>error</code> and above) are what most production alerting should watch.
          </p>
        </div>

        {/* Default locations */}
        <h3 className="text-lg font-semibold text-[#4B4F58] mb-2">Default log file locations</h3>
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 text-sm">
            <tbody>
              {DEFAULT_LOCATIONS.map((loc) => (
                <tr key={loc.path}>
                  <td className="px-3 py-2 border font-mono text-xs text-[#2813e8] whitespace-nowrap">{loc.path}</td>
                  <td className="px-3 py-2 border text-[#4B4F58]">{loc.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Custom log_format */}
        <h3 className="text-lg font-semibold text-[#4B4F58] mb-2">Defining a custom log_format</h3>
        <p className="text-[#4B4F58] mb-3 text-sm">
          Define a named format with the <code className="bg-gray-100 px-1 rounded">log_format</code> directive in
          the <code className="bg-gray-100 px-1 rounded">http {'{}'}</code> block, then reference it on an
          <code className="bg-gray-100 px-1 rounded ml-1">access_log</code> directive. Adding upstream timing
          variables is what lets the &ldquo;slow upstream&rdquo; pattern below work:
        </p>
        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto mb-2">
            {CUSTOM_LOG_FORMAT_EXAMPLE}
          </pre>
          <div className="mb-6">
            <TrackedCopyButton text={CUSTOM_LOG_FORMAT_EXAMPLE} toolSlug={TOOL_SLUG} label="Copy log_format" size="sm" variant="outline" />
          </div>
        </div>
      </section>

      {/* ================= SECTION 2: PATTERN LIBRARY ================= */}
      <section id="pattern-library" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-[#4B4F58] mb-1">Pattern library</h2>
        <p className="text-[#4B4F58] mb-6 text-sm">
          {PATTERNS.length} curated regex patterns for nginx access and error logs. Each card includes the regex,
          what it catches, a matching example line, a severity, and a ready-to-paste{' '}
          <a href="#alert24-config" className="text-[#2813e8] underline">Alert24 server-agent</a> log-search config.
          Click <strong>Test this pattern</strong> to load it into the matcher below.
        </p>

        <div className="space-y-6">
          {PATTERNS.map((p) => (
            <div key={p.id} id={p.id} className="border border-gray-200 rounded-lg p-4 scroll-mt-24">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="text-base font-semibold text-[#4B4F58] mr-auto">{p.name}</h3>
                <LogBadge log={p.log} />
                <SeverityBadge severity={p.severity} />
              </div>

              <p className="text-sm text-[#4B4F58] mb-3">{p.catches}</p>

              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Regex</div>
                <div className="flex items-start gap-2">
                  <code className="flex-1 block bg-gray-900 text-emerald-300 rounded p-2 text-xs overflow-x-auto break-all">
                    {p.regex}
                  </code>
                  <TrackedCopyButton text={p.regex} toolSlug={TOOL_SLUG} label="" showLabel={false} size="sm" variant="outline" />
                </div>
              </div>

              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Example matching line</div>
                <code className="block bg-gray-50 border border-gray-200 rounded p-2 text-xs overflow-x-auto text-[#4B4F58]">
                  {p.example}
                </code>
              </div>

              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Alert24 server-agent config (<code className="lowercase">log_searches</code> entry)
                </div>
                <div className="flex items-start gap-2">
                  <code className="flex-1 block bg-[#0b1020] text-sky-200 rounded p-2 text-xs overflow-x-auto break-all">
                    {p.agentConfig}
                  </code>
                  <TrackedCopyButton text={p.agentConfig} toolSlug={TOOL_SLUG} label="" showLabel={false} size="sm" variant="outline" />
                </div>
              </div>

              {p.alertRule && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                    Matching alert rule (<code className="lowercase">alert_rules</code> entry)
                  </div>
                  <div className="flex items-start gap-2">
                    <code className="flex-1 block bg-[#0b1020] text-amber-200 rounded p-2 text-xs overflow-x-auto break-all">
                      {p.alertRule}
                    </code>
                    <TrackedCopyButton text={p.alertRule} toolSlug={TOOL_SLUG} label="" showLabel={false} size="sm" variant="outline" />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setSelectedId(p.id);
                  if (!input) loadExamples();
                  document.getElementById('paste-and-test')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-1 inline-flex items-center rounded-md bg-[#2813e8] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1f0fb8]"
              >
                Test this pattern &rarr;
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SECTION 3: PASTE AND TEST ================= */}
      <section id="paste-and-test" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-[#4B4F58] mb-1">Paste &amp; test</h2>
        <p className="text-[#4B4F58] mb-4 text-sm">
          Paste sample log lines below and pick a pattern; matches are highlighted live in your browser.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-1">
            <label htmlFor="pattern-select" className="block text-sm font-medium text-[#4B4F58] mb-1">
              Pattern
            </label>
            <select
              id="pattern-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {PATTERNS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.log})
                </option>
              ))}
            </select>
            {selected && (
              <code className="mt-2 block bg-gray-900 text-emerald-300 rounded p-2 text-xs overflow-x-auto break-all">
                /{selected.regex}/{selected.flags}
              </code>
            )}
            <button
              type="button"
              onClick={loadExamples}
              className="mt-2 text-xs text-[#2813e8] underline hover:no-underline"
            >
              Load example log lines
            </button>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="log-input" className="block text-sm font-medium text-[#4B4F58] mb-1">
              Log lines
            </label>
            <textarea
              id="log-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={10}
              spellCheck={false}
              placeholder="Paste nginx access.log or error.log lines here…"
              className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs"
            />
          </div>
        </div>

        {result.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Pattern error: {result.error}
          </div>
        ) : input ? (
          <div>
            <div className="mb-2 text-sm text-[#4B4F58]">
              <strong>{result.matchCount}</strong> match{result.matchCount === 1 ? '' : 'es'} across{' '}
              <strong>{result.matchedLines}</strong> of <strong>{result.totalLines}</strong> non-empty line
              {result.totalLines === 1 ? '' : 's'}.
            </div>
            <pre
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all text-[#4B4F58]"
              // Output is built from escaped input + our own <mark> tags only.
              dangerouslySetInnerHTML={{ __html: result.html }}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500">No log lines yet — paste some above or click &ldquo;Load example log lines.&rdquo;</p>
        )}
      </section>

      {/* ================= ALERT24 / AGENT CONFIG NOTES ================= */}
      <section id="alert24-config" className="mb-10 scroll-mt-24">
        <h2 className="text-2xl font-bold text-[#4B4F58] mb-2">Turning a pattern into a monitor</h2>
        <p className="text-[#4B4F58] mb-3 text-sm">
          The Alert24 server agent can tail these log files and report match counts on every heartbeat, so the
          alert-rule engine can threshold on them (e.g. &ldquo;page me if the 5xx error rate exceeds 5% for 2
          minutes&rdquo;). Each pattern card above includes a <code className="bg-gray-100 px-1 rounded">log_searches</code>
          {' '}entry; drop it into your agent config alongside <code className="bg-gray-100 px-1 rounded">metrics</code>,
          {' '}<code className="bg-gray-100 px-1 rounded">services</code>, and{' '}
          <code className="bg-gray-100 px-1 rounded">alert_rules</code>.
        </p>
        <p className="text-[#4B4F58] mb-3 text-sm">{ALERT_RULES_DOC_NOTE}</p>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Log-search monitoring is a paid (Pro/Enterprise) Alert24 feature. Every plan includes 3 server agents
          free; paid plans add 5 server agents per subscription unit. On a plan without log-search monitoring the
          heartbeat is still accepted &mdash; the <code className="bg-amber-100 px-1 rounded">log_searches</code> are
          simply ignored.
        </div>
      </section>

      {/* CTA */}
      <Alert24CTA variant="log-search" source="nginx-log-patterns" />
    </div>
  );
}
